import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Search, Download, Mail, CheckCircle, XCircle, Clock, FileText, RotateCcw, RefreshCw, FileSpreadsheet, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

export default function StudentResultsDashboard({ user, logout }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [showAddStudentDialog, setShowAddStudentDialog] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: "",
    last_name: "",
    email: "",
    agd_number: ""
  });
  const [showAgdPreviewDialog, setShowAgdPreviewDialog] = useState(false);
  const [agdPreviewData, setAgdPreviewData] = useState(null);
  const [agdLoading, setAgdLoading] = useState(false);
  const [agdDateRange, setAgdDateRange] = useState({
    start: "",
    end: ""
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Auto-refresh when page becomes visible and student results are loaded
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedCourse) {
        fetchStudentResults(selectedCourse.course_id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, { headers });
      setCourses(response.data);
    } catch (error) {
      toast.error("Error loading courses");
    }
  };

  const fetchStudentResults = async (courseId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/student-results`, { headers });
      setStudentResults(response.data.students);
      setSelectedCourse(response.data);
    } catch (error) {
      toast.error("Error loading student results");
    } finally {
      setLoading(false);
    }
  };

  const viewSurveyResponses = async (student) => {
    setSelectedStudent(student);
    
    // Get survey questions
    try {
      const response = await axios.get(`${API}/admin/courses/${selectedCourse.course_id}/survey`, { headers });
      setSurveyQuestions(response.data.questions || []);
    } catch (error) {
      setSurveyQuestions([]);
    }
    
    setShowSurveyDialog(true);
  };

  const deleteCertificate = async (certificateId, studentName) => {
    if (!confirm(`Are you sure you want to delete the completion letter for ${studentName}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/certificates/${certificateId}`, { headers });
      toast.success("Completion letter deleted successfully");
      fetchStudentResults(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to delete completion letter");
    }
  };

  const downloadStudentCertificate = async (studentId, studentName) => {
    try {
      const response = await fetch(`${API}/admin/certificates/${studentId}/${selectedCourse.course_id}/download`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Certificate_${studentName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success('Certificate downloaded');
      } else {
        toast.error('Failed to download certificate');
      }
    } catch {
      toast.error('Failed to download certificate');
    }
  };

  const addStudentToCourse = async () => {
    // Validate fields
    if (!newStudent.first_name.trim() || !newStudent.last_name.trim() || !newStudent.email.trim()) {
      toast.error("Please fill in first name, last name, and email");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/courses/${selectedCourse.course_id}/add-student`,
        {
          first_name: newStudent.first_name.trim(),
          last_name: newStudent.last_name.trim(),
          email: newStudent.email.trim(),
          agd_number: newStudent.agd_number.trim() || "N/A"
        },
        { headers }
      );
      
      if (response.data.new_account) {
        toast.success(`New student account created and added to course! Default password: ChangeMe123!`);
      } else {
        toast.success("Existing student added to course successfully");
      }
      
      setShowAddStudentDialog(false);
      setNewStudent({ first_name: "", last_name: "", email: "", agd_number: "" });
      fetchStudentResults(selectedCourse.course_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add student");
    }
  };

  const removeStudentFromCourse = async (studentId, studentName) => {
    if (!confirm(`Are you sure you want to remove ${studentName} from this course? This will delete ALL their data (progress, assessments, surveys, certificates) for this course.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/courses/${selectedCourse.course_id}/students/${studentId}`, { headers });
      toast.success("Student removed from course successfully");
      fetchStudentResults(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to remove student");
    }
  };


  const resetStudentQuestions = async (studentId, studentName) => {
    if (!confirm(`Reset question requirement for ${studentName}? This will set their questions_asked count to 0 and they will need to ask questions again to unlock the assessment.`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/admin/courses/${selectedCourse.course_id}/students/${studentId}/reset-questions`, {}, { headers });
      toast.success(`Question requirement reset for ${studentName}`);
      fetchStudentResults(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to reset questions");
    }
  };

  // AGD Report Functions
  const previewAgdReport = async (startDate = null, endDate = null) => {
    setAgdLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const url = params.toString() 
        ? `${API}/admin/agd-report/preview?${params}` 
        : `${API}/admin/agd-report/preview`;
      
      const response = await axios.get(url, { headers });
      setAgdPreviewData(response.data);
      setShowAgdPreviewDialog(true);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No students with AGD numbers have completed courses in selected range");
      } else {
        toast.error("Failed to load AGD report preview");
      }
    } finally {
      setAgdLoading(false);
    }
  };

  const downloadAgdReport = async () => {
    try {
      const params = new URLSearchParams();
      if (agdDateRange.start) params.append('start_date', agdDateRange.start);
      if (agdDateRange.end) params.append('end_date', agdDateRange.end);
      
      const url = params.toString() 
        ? `${API}/admin/agd-report?${params}` 
        : `${API}/admin/agd-report`;
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `AGD_Roster_Report_${new Date().toISOString().slice(0, 10)}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("AGD Report downloaded successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No students with AGD numbers have completed courses in selected range");
      } else {
        toast.error("Failed to download AGD report");
      }
    }
  };

  // Helper to set month presets
  const setMonthPreset = (monthsAgo = 0) => {
    const now = new Date();
    const targetMonth = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
    const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
    
    const start = targetMonth.toISOString().slice(0, 10);
    const end = lastDay.toISOString().slice(0, 10);
    
    setAgdDateRange({ start, end });
    previewAgdReport(start, end);
  };


  const filteredResults = studentResults.filter(s =>
    s.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.student_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md border-b-4" style={{ borderColor: '#9E3639' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 sm:h-12 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-2xl font-bold truncate" style={{ color: '#092036' }}>
                  Student Results Dashboard
                </h1>
                <p className="text-xs sm:text-sm text-slate-600 hidden sm:block">View and manage student progress</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAgdDateRange({ start: "", end: "" });
                  setAgdPreviewData(null);
                  setShowAgdPreviewDialog(true);
                }}
                disabled={agdLoading}
                className="gap-1 sm:gap-2 flex-1 sm:flex-initial bg-green-50 border-green-600 text-green-700 hover:bg-green-100"
                data-testid="agd-report-btn"
              >
                <FileSpreadsheet className={`w-3 h-3 sm:w-4 sm:h-4 ${agdLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">AGD Report</span>
                <span className="sm:hidden">AGD</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-1 sm:gap-2 flex-1 sm:flex-initial"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Back to Admin</span>
                <span className="sm:hidden">Admin</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="gap-1 sm:gap-2"
              >
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {!selectedCourse ? (
          <>
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6" style={{ color: '#092036' }}>
              Select a Course to View Results
            </h2>
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
              {courses.map(course => (
                <Card
                  key={course.id}
                  className="p-4 sm:p-6 hover:shadow-xl transition-all cursor-pointer border-2 hover:border-indigo-300 flex flex-col"
                  onClick={() => fetchStudentResults(course.id)}
                >
                  <div className="flex-grow">
                    <h3 className="font-semibold text-base sm:text-lg mb-2" style={{ color: '#092036' }}>
                      {course.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">
                      {course.description?.substring(0, 100)}...
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full text-white mt-auto"
                    style={{ background: '#102862' }}
                  >
                    View Results
                  </Button>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-4 mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#092036' }}>
                  {selectedCourse.course_title}
                </h2>
                <p className="text-sm sm:text-base text-slate-600">
                  {selectedCourse.total_students} student(s) enrolled
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  onClick={() => setShowAddStudentDialog(true)}
                  size="sm"
                  className="gap-1 sm:gap-2 bg-green-600 hover:bg-green-700 w-full"
                >
                  <span className="text-lg">+</span>
                  <span className="hidden sm:inline">Add Student</span>
                  <span className="sm:hidden">Add</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLoading(true);
                    fetchStudentResults(selectedCourse.course_id);
                  }}
                  className="gap-1 sm:gap-2 w-full"
                  disabled={loading}
                >
                  <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedCourse(null);
                    setStudentResults([]);
                  }}
                  className="gap-1 sm:gap-2 w-full"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Back to Courses</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search by student name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Results Table */}
            {loading ? (
              <Card className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 animate-spin text-slate-400" />
                <p className="text-slate-600">Loading student results...</p>
              </Card>
            ) : filteredResults.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">No student results found</p>
              </Card>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-100 border-b-2" style={{ borderColor: '#102862' }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Student</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">State</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">License #</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">AGD #</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Score</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Status</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Survey</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Certificate</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((student, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-slate-900">{student.student_name}</p>
                              <p className="text-xs text-slate-600">{student.student_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center text-sm font-medium">{student.state || "—"}</td>
                          <td className="px-4 py-4 text-center text-sm font-mono text-xs">{student.license_number || "—"}</td>
                          <td className="px-4 py-4 text-center text-sm font-mono text-xs">{student.agd_number || "—"}</td>
                          <td className="px-4 py-4 text-center">
                            {student.assessment_score !== null ? (
                              <span className={`font-semibold ${student.assessment_passed ? 'text-green-600' : 'text-red-600'}`}>
                                {student.assessment_score}%
                              </span>
                            ) : (
                              <span className="text-slate-400">Not taken</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {student.assessment_passed ? (
                              <div className="flex flex-col items-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="text-xs text-green-700 font-medium mt-1">Passed</span>
                                {student.assessment_date && (
                                  <span className="text-xs text-slate-500 mt-0.5">
                                    {new Date(student.assessment_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ) : student.assessment_score !== null ? (
                              <div className="flex flex-col items-center">
                                <XCircle className="w-5 h-5 text-red-600" />
                                <span className="text-xs text-red-700 font-medium mt-1">Failed</span>
                                {student.assessment_date && (
                                  <span className="text-xs text-slate-500 mt-0.5">
                                    {new Date(student.assessment_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <Clock className="w-5 h-5 text-slate-400" />
                                <span className="text-xs text-slate-500 mt-1">Pending</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {student.survey_completed ? (
                              <div className="flex flex-col items-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="text-xs text-green-700 font-medium mt-1">Complete</span>
                                {student.survey_date && (
                                  <span className="text-xs text-slate-500 mt-0.5">
                                    {new Date(student.survey_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <XCircle className="w-5 h-5 text-slate-400" />
                                <span className="text-xs text-slate-500 mt-1">Incomplete</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {student.certificate_issued ? (
                              <div className="flex flex-col items-center gap-1">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="text-xs text-green-700 font-medium">Issued</span>
                                {student.certificate_date && (
                                  <span className="text-xs text-slate-500">
                                    {new Date(student.certificate_date).toLocaleDateString()}
                                  </span>
                                )}
                                <div className="flex gap-1 mt-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => downloadStudentCertificate(student.student_id, student.student_name)}
                                    className="text-xs h-7"
                                  >
                                    Download
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => deleteCertificate(student.certificate_id, student.student_name)}
                                    className="text-xs h-7"
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <XCircle className="w-5 h-5 text-slate-400" />
                                <span className="text-xs text-slate-500 mt-1">Not Issued</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex flex-col gap-2 items-center">
                              {student.survey_completed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewSurveyResponses(student)}
                                  className="text-xs"
                                >
                                  View Survey
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resetStudentQuestions(student.student_id, student.student_name)}
                                className="text-xs"
                                title="Reset question requirement"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Reset Questions
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => removeStudentFromCourse(student.student_id, student.student_name)}
                                className="text-xs"
                              >
                                Remove Student
                              </Button>
                            </div>
                          </td>
                        </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                  {filteredResults.map((student, idx) => (
                    <Card key={idx} className="p-4">
                      {/* Student Info */}
                      <div className="mb-3 pb-3 border-b">
                        <p className="font-semibold text-base text-slate-900">{student.student_name}</p>
                        <p className="text-xs text-slate-600">{student.student_email}</p>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div>
                          <span className="text-slate-500 text-xs">State:</span>
                          <p className="font-medium">{student.state || "—"}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs">License #:</span>
                          <p className="font-medium text-xs">{student.license_number || "—"}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs">AGD #:</span>
                          <p className="font-medium text-xs">{student.agd_number || "—"}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs">Score:</span>
                          <p className="font-semibold">
                            {student.assessment_score !== null ? (
                              <span className={student.assessment_passed ? 'text-green-600' : 'text-red-600'}>
                                {student.assessment_score}%
                              </span>
                            ) : (
                              <span className="text-slate-400">Not taken</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Status Badges */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Status</p>
                          {student.assessment_passed ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-700 font-medium">Passed</span>
                            </div>
                          ) : student.assessment_score !== null ? (
                            <div className="flex flex-col items-center">
                              <XCircle className="w-4 h-4 text-red-600" />
                              <span className="text-xs text-red-700 font-medium">Failed</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-xs text-slate-500">Pending</span>
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Survey</p>
                          {student.survey_completed ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-700 font-medium">Done</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <XCircle className="w-4 h-4 text-slate-400" />
                              <span className="text-xs text-slate-500">Pending</span>
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Cert</p>
                          {student.certificate_issued ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-700 font-medium">Issued</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <XCircle className="w-4 h-4 text-slate-400" />
                              <span className="text-xs text-slate-500">No</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2">
                        {student.survey_completed && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewSurveyResponses(student)}
                            className="text-xs w-full"
                          >
                            View Survey
                          </Button>
                        )}
                        {student.certificate_issued && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadStudentCertificate(student.student_id, student.student_name)}
                            className="text-xs w-full"
                          >
                            Download Cert
                          </Button>
                        )}
                        {student.certificate_issued && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteCertificate(student.certificate_id, student.student_name)}
                            className="text-xs w-full"
                          >
                            Delete Cert
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resetStudentQuestions(student.student_id, student.student_name)}
                          className="text-xs w-full"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Reset Qs
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeStudentFromCourse(student.student_id, student.student_name)}
                          className="text-xs w-full"
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Survey Responses Dialog */}
      <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Survey Responses - {selectedStudent?.student_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedStudent?.survey_answers?.map((answer, idx) => {
              // The answers are now pre-formatted strings like "Label: Value"
              const isFormatted = typeof answer === 'string' && answer.includes(':');
              const label = isFormatted ? answer.split(':')[0].trim() : `Question ${idx + 1}`;
              const value = isFormatted ? answer.split(':').slice(1).join(':').trim() : answer;
              
              return (
                <Card key={idx} className="p-4 bg-slate-50">
                  <p className="font-semibold text-sm mb-1 text-slate-700">
                    {label}
                  </p>
                  <p className="text-slate-900">{value || "—"}</p>
                </Card>
              );
            })}
            {(!selectedStudent?.survey_answers || selectedStudent.survey_answers.length === 0) && (
              <p className="text-center text-slate-600">No survey responses available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={showAddStudentDialog} onOpenChange={setShowAddStudentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Student to Course</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">First Name *</label>
              <Input
                type="text"
                placeholder="John"
                value={newStudent.first_name}
                onChange={(e) => setNewStudent({...newStudent, first_name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Last Name *</label>
              <Input
                type="text"
                placeholder="Doe"
                value={newStudent.last_name}
                onChange={(e) => setNewStudent({...newStudent, last_name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <Input
                type="email"
                placeholder="student@example.com"
                value={newStudent.email}
                onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">AGD Number (Optional)</label>
              <Input
                type="text"
                placeholder="AGD12345"
                value={newStudent.agd_number}
                onChange={(e) => setNewStudent({...newStudent, agd_number: e.target.value})}
              />
            </div>
            <p className="text-xs text-slate-600">
              * If email exists, existing account will be enrolled. If new, account will be created with default password: <strong>ChangeMe123!</strong>
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddStudentDialog(false);
                  setNewStudent({ first_name: "", last_name: "", email: "", agd_number: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={addStudentToCourse}
                className="bg-green-600 hover:bg-green-700"
              >
                Add Student
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AGD Report Preview Dialog */}
      <Dialog open={showAgdPreviewDialog} onOpenChange={setShowAgdPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              AGD Roster Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Date Range Selection */}
            <Card className="p-4 bg-slate-50 border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">Select Date Range</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Start Date</label>
                  <Input
                    type="date"
                    value={agdDateRange.start}
                    onChange={(e) => setAgdDateRange({ ...agdDateRange, start: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">End Date</label>
                  <Input
                    type="date"
                    value={agdDateRange.end}
                    onChange={(e) => setAgdDateRange({ ...agdDateRange, end: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>
              
              {/* Quick Preset Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMonthPreset(0)}
                  className="text-xs"
                >
                  This Month
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMonthPreset(1)}
                  className="text-xs"
                >
                  Last Month
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMonthPreset(2)}
                  className="text-xs"
                >
                  2 Months Ago
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAgdDateRange({ start: "", end: "" });
                    previewAgdReport();
                  }}
                  className="text-xs"
                >
                  All Time
                </Button>
              </div>
              
              <Button
                onClick={() => previewAgdReport(agdDateRange.start, agdDateRange.end)}
                disabled={agdLoading}
                className="w-full bg-slate-700 hover:bg-slate-800"
              >
                {agdLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Generate Preview
                  </>
                )}
              </Button>
            </Card>

            {agdPreviewData && (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-slate-50">
                    <p className="text-sm text-slate-600">Total Certificates{agdPreviewData.date_range?.start || agdPreviewData.date_range?.end ? " (Filtered)" : ""}</p>
                    <p className="text-2xl font-bold text-slate-900">{agdPreviewData.total_certificates}</p>
                  </Card>
                  <Card className="p-4 bg-green-50 border-green-200">
                    <p className="text-sm text-green-700">AGD Eligible (Have AGD #)</p>
                    <p className="text-2xl font-bold text-green-700">{agdPreviewData.agd_eligible}</p>
                  </Card>
                </div>

                {agdPreviewData.agd_eligible === 0 ? (
                  <Card className="p-8 text-center bg-amber-50 border-amber-200">
                    <Users className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                    <p className="text-amber-700 font-medium">
                      No students with AGD numbers have completed courses
                      {(agdDateRange.start || agdDateRange.end) ? " in the selected date range" : ""}.
                    </p>
                    <p className="text-sm text-amber-600 mt-2">
                      Try adjusting the date range or ensure students have added their AGD number to their profile.
                    </p>
                  </Card>
                ) : (
                  <>
                    {/* Preview Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 border-b">
                        <p className="text-sm font-medium text-slate-700">
                          Students to be included in CSV ({agdPreviewData.students?.length || 0} records)
                          {agdPreviewData.date_range?.start && (
                            <span className="text-slate-500 ml-2">
                              | From: {agdPreviewData.date_range.start}
                            </span>
                          )}
                          {agdPreviewData.date_range?.end && (
                            <span className="text-slate-500 ml-2">
                              | To: {agdPreviewData.date_range.end}
                            </span>
                          )}
                        </p>
                      </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-700">Student</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-700">AGD #</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-700">Course</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-700">CE Hours</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-700">Subject Code</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-700">Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agdPreviewData.students?.map((student, idx) => (
                            <tr key={idx} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium">{student.student_name}</td>
                              <td className="px-3 py-2 font-mono text-xs">{student.agd_number}</td>
                              <td className="px-3 py-2">{student.course_title}</td>
                              <td className="px-3 py-2 text-center">{student.ce_hours}</td>
                              <td className="px-3 py-2 font-mono text-xs">{student.subject_code || "—"}</td>
                              <td className="px-3 py-2">{student.completion_date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* CSV Format Info */}
                  <Card className="p-4 bg-blue-50 border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-2">CSV Format (per AGD guidelines):</p>
                    <p className="text-xs text-blue-700 font-mono">
                      AGD Member ID, First Name, Last Name, Completion Date, CE Hours, Subject Code, Verification Code
                    </p>
                  </Card>

                  {/* Download Button */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowAgdPreviewDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        downloadAgdReport();
                        setShowAgdPreviewDialog(false);
                      }}
                      className="bg-green-600 hover:bg-green-700 gap-2"
                      data-testid="download-agd-csv-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </Button>
                  </div>
                </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
